import { makeStyles } from 'tss-react/mui';

import { Box, Button, Theme, Typography, useTheme } from '@mui/material';

import {
  FirmBankingSystemAccountData,
  SYSTEM_ACCOUNT_LABELS,
  SWEEP_STATUSES,
  SYSTEM_ACCOUNT_STATUSES,
} from './types';

import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { capitalizeText } from '../../helpers/capitalizeText';
import { Dispatch, SetStateAction } from 'react';
import { useHasPermission } from '../../hooks/useHasPermission';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useNavigate } from 'react-router-dom';
import { StatusChip } from '../../components/StatusChip';

const hideAutoSweep = false;

const useStyles = makeStyles<{ chipColor?: string }>()((theme: Theme, { chipColor }) => ({
  systemAccountWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '20px',
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    minHeight: '200px',
  },
  systemAccountsColoredLabel: {
    color: 'rgba(37, 157, 168, 0.72)',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '120%',
    fontFamily: 'Poppins',
  },
  systemAccountsSmallLabel: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '120%',
    color: '#93959E',
    letterSpacing: '0.2px',
    fontFamily: 'Inter',
  },
    systemAccountsProjectedBalance: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '120%',
    color: '#93959E',
    letterSpacing: '0.2px',
    fontFamily: 'Inter',
    marginBottom: '12px',
  },
  hiddenBalancePlaceholder: {
    visibility: 'hidden',
  },
  systemAccountsLabel: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  systemAccountsBalanceText: {
    fontSize: '34px',
    fontWeight: 600,
    lineHeight: '120%',
    fontFamily: 'Poppins',
    color: theme.palette.common.white,
    marginTop: '4px',
    marginBottom: '12px',
  },
  noAccountText: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '19px',
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.3)',
    maxWidth: '260px',
    fontFamily: 'Inter',
  },
  systemAccountStatusChip: {
    padding: '4px 12px',
    borderRadius: '12px',
    border: `1px solid ${chipColor}`,
    background: `${chipColor}19`,
    color: chipColor,
    fontWeight: 500,
    fontSize: '14px',
    fontFamily: 'Inter',
    lineHeight: '120%',
  },
  accountsInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    justifyContent: 'flex-end',
    marginTop: '28px',
  },
  withdrawButton: {
    background: theme.palette.info.main,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.info.main}`,
    padding: '10px 32px',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    fontFamily: 'Inter',
    color: theme.palette.common.white,
    textTransform: 'none',

    '&:hover': {
      cursor: 'pointer',
      background: theme.palette.info.main,
      border: `1px solid ${theme.palette.info.main}`,
    },

    '&.Mui-disabled': {
      border: 'none',
      color: theme.palette.text.disabled,
      background: 'rgba(255, 255, 255, 0.08)',
    },
  },
  autoSweepButton: {
    background: theme.palette.primary.light,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    padding: '8px 28px',
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: '120%',
    fontFamily: 'Inter',
    color: theme.palette.common.white,
    textTransform: 'none',

    '&:hover': {
      cursor: 'pointer',
      background: theme.palette.secondary.light,
      border: `1px solid ${theme.palette.secondary.dark}`,
    },

    '&.Mui-disabled': {
      border: 'none',
      color: theme.palette.text.disabled,
      background: 'rgba(255, 255, 255, 0.08)',
    },
  },
}));

interface FirmBankingSystemAccountContainerProps {
  accountType: string;
  accountData: FirmBankingSystemAccountData | null;
  setSelectedAccountType: Dispatch<SetStateAction<string>>;
  setIsWithdrawalPopupOpen: Dispatch<SetStateAction<boolean>>;
}

const FirmBankingSystemAccountContainer = ({
  accountData,
  accountType,
  setSelectedAccountType,
  setIsWithdrawalPopupOpen,
}:

  FirmBankingSystemAccountContainerProps) => {
  const { classes } = useStyles({ chipColor: '' });
  const navigate = useNavigate();
  const canEdit = useHasPermission('Banking', 'Edit');

  const formattedLabel = SYSTEM_ACCOUNT_LABELS[accountType as keyof typeof SYSTEM_ACCOUNT_LABELS];
  const isAutoWithdrawalDisabled = accountData?.sweep?.status === SWEEP_STATUSES.DISABLED;

  const onWithdrawButtonClick = () => {
    // setAlert((prev) => ({
    //   ...prev,
    //   message: 'Contact Admin to withdraw funds.',
    //   type: 'info',
    // }));

    setIsWithdrawalPopupOpen(true);
    setSelectedAccountType(accountType);
  };

  const onAutoSweepButtonClick = () => {
    navigate('/banking/accounts/autosweep', {
      state: {
        accountData,
        accountType
      },
    });
  };

  const parsedLedgerBalance = Number(accountData?.balance);
  const parsedProjectedBalance = Number(accountData?.projectedBalance);
  const isProjectedBalanceSame =
    !Number.isNaN(parsedLedgerBalance) &&
    !Number.isNaN(parsedProjectedBalance)
      ? Math.abs(parsedProjectedBalance - parsedLedgerBalance) < 0.005
      : accountData?.projectedBalance === accountData?.balance;

  if (!accountData) {
    return (
      <Box className={classes.systemAccountWrapper}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px"></Box>
        <Typography className={classes.systemAccountsColoredLabel}>{formattedLabel}</Typography>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Typography className={classes.noAccountText}>
            No information about this account, or it has not been created yet.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.systemAccountWrapper}>
      {accountType === 'case' ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px">
            <Typography className={classes.systemAccountsColoredLabel}>{formattedLabel}</Typography>

            <SystemAccountStatusChip status={accountData.status} />
          </Box>

          <Box display="flex" flexDirection="column" justifyContent="center" width="100%">
            <Typography className={classes.systemAccountsBalanceText}>
              {numberToUSDCurrency(accountData.balance)}
            </Typography>
          </Box>
        </>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px">
            <Typography className={classes.systemAccountsColoredLabel}>{formattedLabel}</Typography>

            <SystemAccountStatusChip status={accountData.status} />
          </Box>

          <Box className={classes.accountsInfoContainer}>
            <Typography
              className={classes.systemAccountsSmallLabel}
            >{`Account Number: ${accountData.accountNumber}`}</Typography>

            <Typography className={classes.systemAccountsSmallLabel}>
              {`Routing Number (ABA):${accountData.routingNumber}`}
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="center"
            width="100%"
            flexDirection="column"
            marginTop="28px"
          >
            <Typography className={classes.systemAccountsSmallLabel}>
              Ledger Balance
            </Typography>
            <Typography className={classes.systemAccountsBalanceText}>
              {numberToUSDCurrency(accountData.balance)}
            </Typography>
            <Typography
              className={classes.systemAccountsProjectedBalance}
              aria-hidden={isProjectedBalanceSame}
              sx={isProjectedBalanceSame ? { visibility: 'hidden' } : undefined}
            >
              {`Projected: ${numberToUSDCurrency(accountData.projectedBalance)}`}
            </Typography>
            {canEdit && (
              <Button
                disableRipple
                className={classes.withdrawButton}
                disabled={
                  !accountData.withdrawalAvailable ||
                  Number(accountData.balance) === 0 ||
                  !isAutoWithdrawalDisabled
                }
                onClick={onWithdrawButtonClick}
              >
                Withdraw Funds
              </Button>
            )}

            {!hideAutoSweep && <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: '10px', alignItems: 'center' }}>              
                <Button
                  disableRipple
                  className={classes.autoSweepButton}
                  disabled={false}
                  onClick={onAutoSweepButtonClick}
                >
                  <SyncAltIcon sx={{ marginRight: '5px' }} />  Auto-Sweep
                </Button>
             
              {accountData?.sweep?.status &&
                <StatusChip
                  label={accountData?.sweep?.status?.toUpperCase() === 'ENABLED' ? `ON · ${accountData?.sweep?.accountNumber} ` : accountData?.sweep?.status?.toUpperCase() === 'DISABLED' ? 'OFF' : accountData?.sweep?.status?.toUpperCase() === 'NEEDS_ATTENTION' ? 'Action Needed' : 'NOT APPLICABLE'}
                  color={accountData?.sweep?.status?.toUpperCase() === 'ENABLED' ? '#88B856' :  accountData?.sweep?.status?.toUpperCase() === 'DISABLED' ? '#FFF' : accountData?.sweep?.status?.toUpperCase() === 'NEEDS_ATTENTION' ? '#FFA500' : '#CCC'}
                />
              }
            </Box>}
          </Box>
        </>
      )}
    </Box>
  );
};

interface SystemAccountStatusChipProps {
  status: string;
}

const SystemAccountStatusChip = ({ status }: SystemAccountStatusChipProps) => {
  const theme = useTheme();

  const SystemAccountStatusesChipColor = {
    CLOSED: theme.palette.error.main,
    BLOCKED: theme.palette.error.main,
    CLOSURE_INITIATED: theme.palette.warning.main,
    INACTIVE: theme.palette.warning.main,
    ACTIVE: theme.palette.success.main,
  };

  const currentChipColor =
    SystemAccountStatusesChipColor[status as keyof typeof SYSTEM_ACCOUNT_STATUSES];

  const styles = useStyles({ chipColor: currentChipColor });

  return (
    <Box className={styles.classes.systemAccountStatusChip}>
      {capitalizeText(SYSTEM_ACCOUNT_STATUSES[status as keyof typeof SYSTEM_ACCOUNT_STATUSES])}
    </Box>
  );
};

export default FirmBankingSystemAccountContainer;
